// The building map's data (concept D): flat plans of the real building, one floor at a time, drawn by
// frontend/art/mapplan.py into /world/plan/. Everything here is in plan pixels (the plan images' own pixels).
import type { FloorId } from './assets';
import { worldUrl } from './version';

export type Level = 'G' | '1' | '2' | '3' | '4';
export type Box = [number, number, number, number];
export type Pt = [number, number];

export interface PlanRoom {
  floor: Level; rect: Box; name: string; holds: string; fill: string; ink: string; big: boolean;
  door: Pt | null; picture?: string;
}
export interface PlanMeta {
  size: [number, number];
  /** Walks along the corridors: per floor, from each room's door to the floor's hub. */
  paths: Partial<Record<Level, Record<string, Pt[]>>>;
  /** Per floor, from the hub to the lift. */
  liftPaths: Partial<Record<Level, Pt[]>>;
  floors: Array<{ id: Level; name: string; img: string; thumb: string }>;
  rooms: Record<string, PlanRoom>;
  labels: Array<{ floor: Level; rect: Box; text: string }>;
  lifts: Array<{ floor: Level; id: string; at: Pt; levels: Level[] }>;
  exits: Partial<Record<Level, { rect: Box; door: Pt }>>;
  front: { name: string; holds: string; picture?: string };
}

/** A plan picture or floor sheet, by its name under /world/ (versioned with the art, see version.ts). */
export const planAsset = (name: string): string => worldUrl(name);
/** Top floor first, as a lift panel reads. */
export const LEVELS_DOWN: Level[] = ['4', '3', '2', '1', 'G'];
export const LEVEL_WORD: Record<Level, string> = { G: 'ground', 1: 'first', 2: 'second', 3: 'third', 4: 'fourth' };
/** The world's floors on the plan: the library is on the third floor and the basketball court on the fourth (the Court's
 *  own numbering; the world's h5 key predates it). The west front is reached through the first floor's bronze doors. */
export const LEVEL_OF_FLOOR: Record<FloorId, Level> = { g: 'G', f1: '1', l3: '3', h5: '4', ext: '1' };

let planPromise: Promise<PlanMeta> | null = null;
export function loadPlan(): Promise<PlanMeta> {
  if (!planPromise) {
    planPromise = fetch(worldUrl('plan/plan.json')).then((r) => {
      if (!r.ok) throw new Error(`plan.json ${r.status}`);
      return r.json() as Promise<PlanMeta>;
    });
    planPromise.catch(() => { planPromise = null; });
  }
  return planPromise;
}

/** The floor a room is on; the west front counts as the floor of the other end of the trip (it has a way out on both). */
export function levelOf(plan: PlanMeta, room: string, other?: string): Level | null {
  if (room === 'front') {
    const o = other && other !== 'front' ? plan.rooms[other]?.floor : undefined;
    return o === 'G' ? 'G' : '1';
  }
  return plan.rooms[room]?.floor ?? null;
}

const same = (p: Pt, q: Pt) => p[0] === q[0] && p[1] === q[1];

/** `a` runs from its start to a hub, `b` from its start (the far end) to a hub: the walk from a's start to b's start, joined
 *  where the two first meet so the shared corridor is not walked twice. */
export function join(a: Pt[], b: Pt[]): Pt[] {
  for (let i = 0; i < a.length; i += 1) {
    const j = b.findIndex((q) => same(q, a[i]));
    if (j >= 0) return [...a.slice(0, i + 1), ...b.slice(0, j).reverse()];
  }
  return [...a, ...[...b].reverse()];
}

export interface Route {
  /** The part of the walk drawn on this floor, along the corridors. */
  points: Pt[];
  /** Where the walk leaves this floor, for the tag at the lift ("up to third"). */
  lift?: { at: Pt; tag: string };
}

/** The walk from one room to another, as seen on `level`; null when neither end is on it. */
export function route(plan: PlanMeta, from: string, to: string, level: Level): Route | null {
  if (from === to) return null;
  const a = levelOf(plan, from, to);
  const b = levelOf(plan, to, from);
  if (!a || !b || (level !== a && level !== b)) return null;
  const paths = plan.paths[level] ?? {};
  const lift = plan.liftPaths[level];
  if (a === b) {
    const p = paths[from]; const q = paths[to];
    return p && q ? { points: join(p, q) } : null;
  }
  if (!lift || lift.length === 0) return null;
  const toLift = [...lift].reverse();
  if (level === a) {
    const p = paths[from];
    const up = LEVELS_DOWN.indexOf(b) < LEVELS_DOWN.indexOf(a);
    return p ? { points: join(p, toLift), lift: { at: lift[lift.length - 1], tag: `${up ? 'up' : 'down'} to ${LEVEL_WORD[b]}` } } : null;
  }
  const q = paths[to];
  return q ? { points: join(toLift, q) } : null;
}

/** How to get there, in one line, when the room is on another floor. */
export function howTo(plan: PlanMeta, from: string, to: string): string | null {
  const a = levelOf(plan, from, to);
  const b = levelOf(plan, to, from);
  if (!a || !b || a === b) return null;
  const up = LEVELS_DOWN.indexOf(b) < LEVELS_DOWN.indexOf(a);
  return `Take the lift ${up ? 'up' : 'down'} to the ${LEVEL_WORD[b]} floor.`;
}
